import { BEHAVIOUR_LABELS } from "@healthos/shared";
import { X } from "@phosphor-icons/react";
import type { CSSProperties } from "react";
import { cn } from "../../lib/cn";
import { CARD_EYEBROW } from "../../primitives/type";
import { MICROSTEP_BEHAVIOUR_VISUALS } from "../microstep-behaviour-visual";
import type { AttentionCardLargeData, AttentionTier } from "./types";

type AttentionMetaProps = {
  data: AttentionCardLargeData;
  inkSecondary: string;
  tier: AttentionTier;
  tierLabel: string;
};

export function AttentionHeader({
  data,
  inkSecondary,
  onDismiss,
  opacityMix,
  tier,
  tierLabel,
}: AttentionMetaProps & {
  onDismiss?: () => void;
  opacityMix: (pct: number) => string;
}) {
  return (
    // z-20, not z-10: the behaviour-icon tooltip drops below the header, and
    // the card body that follows is its own z-10 stacking context. At equal z
    // the later sibling paints on top and the body's anchor line renders
    // through the tooltip.
    <header className={cn("relative z-20 flex items-center justify-between gap-3")}>
      <AttentionMetaLine data={data} ink={inkSecondary} tier={tier} tierLabel={tierLabel} />
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="grid h-7 w-7 place-items-center rounded-md [transition:background-color_200ms_var(--ease-out-expo),color_200ms_var(--ease-out-expo),transform_120ms_var(--ease-out-expo)] active:scale-[0.92]"
          style={{ color: opacityMix(60), backgroundColor: "transparent" }}
          onMouseEnter={(event) => {
            event.currentTarget.style.backgroundColor = opacityMix(10);
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <X size={16} weight="bold" />
        </button>
      ) : null}
    </header>
  );
}

export function MediaMetaRow({ data, inkSecondary, tier, tierLabel }: AttentionMetaProps) {
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <AttentionMetaLine data={data} ink={inkSecondary} tier={tier} tierLabel={tierLabel} />
    </div>
  );
}

function AttentionMetaLine({
  data,
  ink,
  tier,
  tierLabel,
}: {
  data: AttentionCardLargeData;
  ink: string;
  tier: AttentionTier;
  tierLabel: string;
}) {
  const behaviourVisual = data.behaviour ? MICROSTEP_BEHAVIOUR_VISUALS[data.behaviour] : null;
  const showEyebrowText = Boolean(data.eyebrow);

  return (
    <span
      className={cn(CARD_EYEBROW, "inline-flex min-w-0 items-center gap-2 whitespace-nowrap")}
      style={{ color: ink }}
    >
      {!behaviourVisual ? (
        <span
          aria-hidden="true"
          className={cn(
            "attention-eyebrow-dot h-[6px] w-[6px] shrink-0 rounded-full",
            tier === "important" && "motion-safe:animate-gentle-pulse",
          )}
        />
      ) : null}
      {behaviourVisual ? (
        <span
          className="group relative inline-flex shrink-0"
          role="img"
          aria-label={data.behaviourHint ?? data.eyebrow}
        >
          <span
            aria-hidden="true"
            className="attention-behaviour-badge grid h-6 w-6 place-items-center rounded-full border"
            style={{ "--attn-behaviour": behaviourVisual.color } as CSSProperties}
          >
            <behaviourVisual.Icon size={13} weight="duotone" />
          </span>
          {data.behaviourHint ? (
            // w-max + whitespace-normal: the meta line is whitespace-nowrap,
            // and a fixed width let the inherited nowrap run the hint past
            // its own background.
            <span className="pointer-events-none absolute left-0 top-full z-20 mt-2 hidden w-max max-w-[260px] whitespace-normal rounded-lg border border-mist bg-paper px-3 py-2 text-left font-sans text-xs font-medium normal-case leading-snug tracking-normal text-gray-600 shadow-card group-hover:block">
              {data.behaviourHint}
            </span>
          ) : null}
        </span>
      ) : null}
      {showEyebrowText ? <span className="min-w-0 truncate">{data.eyebrow}</span> : null}
      {data.behaviour ? (
        <>
          {showEyebrowText ? (
            <span aria-hidden="true" className="shrink-0 opacity-40">
              ·
            </span>
          ) : null}
          <span className="min-w-0 truncate">{BEHAVIOUR_LABELS[data.behaviour]}</span>
        </>
      ) : null}
      {data.showTier === false ? null : (
        <span
          className={cn(
            CARD_EYEBROW,
            "attention-tier-chip ml-1 inline-flex shrink-0 items-center rounded-full px-2 py-0.5",
          )}
        >
          {tierLabel}
        </span>
      )}
    </span>
  );
}
